import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { dollarFormat, getPropByString, paginationDropdownOptions, toTitleCase } from 'utils/utils';
import { useTrustAccountList } from './hooks/useTrustAccountList';
import { tableCell } from 'modules/reports/components/TableCell/TableCell';
import { useCaseListFilter as useFilter } from 'modules/cases/hooks/useCaseListFilter';
import { useAccounts } from 'hooks/useAccounts';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { DisburseFundModal } from './TemporaryFund/components/WithdrawOrDepositTemporaryFund/DisburseFundModal/DisburseFundModal';
import constants from 'constants/index';
import { TransferFundModal } from './TemporaryFund/components/WithdrawOrDepositTemporaryFund/TransferFundModal/TransferFundModal';
import { LoaderComponent } from 'shared/LoaderComponent';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';

const TrustAccounting = ({ caseDetails, caseView, getCaseDetails }) => {
  const { accountList, isAccountsLoading } = useAccounts({ accountType: 'trust', excludeFromLoading: true });
  const { userDetails } = useUserDetailsContext();
  const [showDisburseFundModal, setShowDisburseFundModal] = useState(false);
  const [showTransferFundModal, setShowTransferFundModal] = useState(false);

  const { filter, filterOnChange, refreshList } = useFilter({
    case_id: caseView ? caseDetails?.case_id : null,
  });
  const {
    trustLogs,
    loadTrustLogs,
    hasNext,
    hasPrevious,
    fetchNext,
    fetchprev,
    onPageChange,
    pageSize,
    loadTrustAccountBalance,
    trustBalance,
    openingBalance,
    reconciledBalance,
  } = useTrustAccountList({
    _filter: filter,
  });

  useEffect(() => {
    loadTrustLogs({ filter });
    if (caseDetails?.default_trust_account?.[0]?.account_id || filter?.account_id) {
      loadTrustAccountBalance(
        filter?.account_id || caseDetails?.default_trust_account?.[0]?.account_id,
        caseView ? caseDetails?.case_id : null
      );
    }
  }, [filter]);

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: () => {
      return (
        <React.Fragment>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Item Per Page
          </span>
          <Dropdown value={pageSize} options={paginationDropdownOptions} onChange={onPageChange} />
        </React.Fragment>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchNext} disabled={hasNext} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchprev} disabled={hasPrevious} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
  };

  const fromTrustBody = (rowData) => {
    let entry_type = rowData?.entry_type;
    return (
      <>
        <span className="p-column-title text-break">Amount</span>
        <span>
          <span className={entry_type === 'cr' ? 'text-success' : 'text-danger'} title={dollarFormat(rowData?.amount)}>
            {dollarFormat(rowData?.amount)}
          </span>
        </span>
      </>
    );
  };

  const toBusinessBody = (rowData) => {
    if (rowData?.entry_type !== 'cr') {
      return (
        <>
          <span className="p-column-title text-break">Paid To</span>
          <span>
            {rowData?.credit_account_name ? (
              <Link to="/settings/accounts" className="call-back text-decoration-none" title={rowData?.credit_account_name}>
                {rowData?.credit_account_name}
              </Link>
            ) : (
              <Link
                to={`/contacts/${
                  rowData?.provider?.contact_id ||
                  rowData?.vendor?.contact_id ||
                  rowData?.checked_contact?.contact_id ||
                  rowData?.contact_id ||
                  rowData?.referral_contact?.contact_id
                }`}
                className="call-back text-decoration-none"
                title={
                  rowData?.provider?.title ||
                  rowData?.vendor?.title ||
                  rowData?.checked_contact?.fullName ||
                  rowData?.contact_name ||
                  rowData?.referral_contact?.fullName
                }
              >
                {rowData?.provider?.title ||
                  rowData?.vendor?.title ||
                  rowData?.checked_contact?.fullName ||
                  rowData?.contact_name ||
                  rowData?.referral_contact?.fullName}
              </Link>
            )}
          </span>
        </>
      );
    }
  };

  const paymentTypeBody = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Payment Type</span>
        <span title={toTitleCase(rowData?.payment_type === 'split_settlement' ? 'Settlement' : rowData?.payment_type)}>
          {toTitleCase(rowData?.payment_type === 'split_settlement' ? 'Settlement' : rowData?.payment_type)}
        </span>
      </>
    );
  };

  const dateBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Date of Transaction</span>
        <span
          className="text-break"
          title={
            rowData?.record_created_date
              ? handleDateTimeOffset(userDetails?.timezone, rowData.record_created_date, constants.month_date_year_format)
              : undefined
          }
        >
          {rowData?.record_created_date
            ? handleDateTimeOffset(userDetails?.timezone, rowData.record_created_date, constants.month_date_year_format)
            : '-'}
        </span>
      </React.Fragment>
    );
  };

  const accountBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid From</span>
        <span className="text-break">
          <Link
            to="/settings/accounts"
            className="call-back text-decoration-none"
            title={rowData?.debit_account_name ? rowData?.debit_account_name : undefined}
          >
            {rowData?.debit_account_name ? rowData?.debit_account_name : '-'}
          </Link>
        </span>
      </React.Fragment>
    );
  };

  const caseTemplate = (rowData, ...rest) => {
    const { header, field } = rest?.[0] || {};
    return (
      <React.Fragment>
        <span className="p-column-title text-break">{header}</span>
        <span className="text-break">
          <Link
            className="cursor-pointer text-decoration-none F-size14 call-back"
            to={'/cases/' + rowData?.case_id}
            title={getPropByString(rowData, field)}
          >
            {getPropByString(rowData, field)}
          </Link>
        </span>
      </React.Fragment>
    );
  };

  const rowClassName = (rowData) => ({ strikethrough: rowData.is_strikethrough });

  const refreshData = () => {
    refreshList({
      case_id: caseView ? caseDetails?.case_id : null,
    });
  };

  const disburseFundCallback = () => {
    typeof getCaseDetails === 'function' && getCaseDetails();
    refreshData();
  };

  const transferFundCallback = () => {
    typeof getCaseDetails === 'function' && getCaseDetails();
    refreshData();
  };

  return (
    <>
      {caseView && (
        <div className="d-flex justify-content-end align-items-center mb-3">
          <Button
            label="Disburse Funds"
            className="p-button p-button-primary me-2"
            onClick={() => setShowDisburseFundModal(true)}
            disabled={Number(trustBalance) === 0}
          />
          {caseDetails?.billing_preferences?.payment_structure === 'Flat Fee' && (
            <Button
              label="Transfer Funds"
              className="p-button p-button-primary"
              onClick={() => setShowTransferFundModal(true)}
              disabled={Number(trustBalance) <= 0}
            />
          )}
        </div>
      )}
      <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-lg-0 p-2 w-100 align-items-center justify-content-between">
        <div className="d-flex flex-wrap align-items-center p-2 filter-wraper">
          {(filter?.account_id || caseView) && (
            <label className="p-0 m-0 filter-label">
              Balance:{' '}
              <span className="text-bold black-600" style={{ marginRight: '10px' }}>
                {dollarFormat(trustBalance)}
              </span>
            </label>
          )}
          {filter?.account_id && !caseView && openingBalance !== 0 && (
            <label className="p-0 m-0 filter-label mr-2">
              Opening Balance:{' '}
              <span className="text-bold black-600" style={{ marginRight: '10px' }}>
                {dollarFormat(openingBalance)}
              </span>
            </label>
          )}
          {filter?.account_id && !caseView && reconciledBalance !== 0 && (
            <label className="p-0 m-0 filter-label">
              Reconciled Balance:{' '}
              <span className="text-bold black-600" style={{ marginRight: '10px' }}>
                {dollarFormat(reconciledBalance)}
              </span>
            </label>
          )}
        </div>
        <div className="d-flex flex-wrap align-items-center p-2 filter-wraper">
          <label className="text-bold black-600 p-0 m-0 filter-label">Filter:</label>
          <div className="d-flex flex-column ms-2 filter-wraper">
            <Calendar
              className="input-shadow filter-calender"
              id="icon"
              name="dateFilter"
              selectionMode="range"
              value={filter?.dateFilter}
              onChange={filterOnChange}
              showIcon
              placeholder="Date"
              maxDate={new Date()}
            />
          </div>
          {!caseView && (
            <div className="d-flex flex-column ms-2 filter-wraper">
              <Dropdown
                placeholder="By Account"
                name="account_id"
                options={accountList}
                value={filter?.account_id}
                onChange={filterOnChange}
                optionLabel={'account_name'}
                optionValue="account_id"
                className="w-100"
                filter
                showClear
                panelFooterTemplate={isAccountsLoading ? LoaderComponent : null}
              />
            </div>
          )}
          <div className="d-flex flex-column mx-2">
            <div className="pointer ms-0">
              <i className="fas fa-sync-alt" onClick={refreshData}></i>
              <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
            </div>
          </div>
        </div>
      </div>

      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={trustLogs}
          className="p-datatable-responsive"
          rowClassName={rowClassName}
          paginatorTemplate={paginatorTemplate}
          paginator
          storageKey="trust_accounting_column_widths"
          columns={[
            { field: 'case_name', header: 'Case Name', body: caseTemplate, bodyClassName: 'ellipsis-text', hidden: caseView },
            { field: 'record_created_date', header: 'Date of Transaction', body: dateBody, bodyClassName: 'ellipsis-text' },
            { field: 'debit_account_name', header: 'Paid From', body: accountBody, bodyClassName: 'ellipsis-text' },
            { field: 'payment_type', header: 'Payment Type', body: paymentTypeBody, bodyClassName: 'ellipsis-text' },
            { field: 'from_trust', header: 'Amount', body: fromTrustBody, bodyClassName: 'ellipsis-text' },
            { field: 'to_business', header: 'Paid To', body: toBusinessBody, bodyClassName: 'ellipsis-text' },
            { field: 'related_to', header: 'Ref.', body: tableCell, bodyClassName: 'ellipsis-text' },
          ]}
        />
      </div>
      {showDisburseFundModal && (
        <DisburseFundModal caseDetails={caseDetails} onHide={() => setShowDisburseFundModal(false)} callback={disburseFundCallback} />
      )}
      {showTransferFundModal && (
        <TransferFundModal
          caseDetails={caseDetails}
          onHide={() => setShowTransferFundModal(false)}
          callback={transferFundCallback}
          trustBalance={trustBalance}
        />
      )}
    </>
  );
};

export default TrustAccounting;
